import { useCallback, useEffect, useRef, useState } from 'react';

interface UseIndicatorContentTransitionProps<T> {
  value: T;
  duration: number;
  shouldSkipTransition: (oldValue: T, newValue: T) => boolean;
  measureElement: () => number | undefined;
}

export function useIndicatorContentTransition<T>({
  value,
  duration,
  shouldSkipTransition,
  measureElement,
}: UseIndicatorContentTransitionProps<T>) {
  const [displayedValue, setDisplayedValue] = useState<T>(value);
  const [opacity, setOpacity] = useState(1);
  const [targetWidth, setTargetWidth] = useState<number | undefined>(undefined);

  const animationStateRef = useRef({
    currentTransitionId: 0,
    isTransitioning: false,
    pendingValue: null as T | null,
  });

  const cancelTransitionRef = useRef<() => void>(() => {});

  const startTransition = useCallback(
    (newValue: T, oldValue: T) => {
      const state = animationStateRef.current;

      cancelTransitionRef.current();

      if (shouldSkipTransition(oldValue, newValue)) {
        setOpacity(1);
        setDisplayedValue(newValue);
        setTargetWidth(measureElement());
        state.isTransitioning = false;
        state.pendingValue = null;
        return;
      }

      state.isTransitioning = true;
      const transitionId = ++state.currentTransitionId;
      setOpacity(0);

      let transitionCompleteTimeout: number;
      let rafId1: number;
      let rafId2: number;

      const fadeOutTimeout = setTimeout(() => {
        if (transitionId !== state.currentTransitionId) {
          return;
        }

        setDisplayedValue(newValue);

        rafId1 = requestAnimationFrame(() => {
          if (transitionId !== state.currentTransitionId) {
            return;
          }

          const newWidth = measureElement();
          setTargetWidth(newWidth);

          rafId2 = requestAnimationFrame(() => {
            if (transitionId !== state.currentTransitionId) {
              return;
            }

            setOpacity(1);

            transitionCompleteTimeout = setTimeout(() => {
              if (transitionId !== state.currentTransitionId) {
                return;
              }

              state.isTransitioning = false;

              if (state.pendingValue) {
                const pendingValue = state.pendingValue;
                state.pendingValue = null;
                // eslint-disable-next-line react-hooks/immutability
                startTransition(pendingValue, newValue);
              }
            }, duration);
          });
        });
      }, duration / 2);

      // Clear both timeouts when unmounting or when the dependencies change
      cancelTransitionRef.current = () => {
        clearTimeout(fadeOutTimeout);
        clearTimeout(transitionCompleteTimeout);
        cancelAnimationFrame(rafId1);
        cancelAnimationFrame(rafId2);
      };
    },
    [duration, measureElement, shouldSkipTransition]
  );

  useEffect(() => {
    if (value === displayedValue) {
      return;
    }

    const state = animationStateRef.current;

    if (state.isTransitioning) {
      state.pendingValue = value;
      return;
    }

    startTransition(value, displayedValue);
  }, [value, displayedValue, startTransition]);

  useEffect(() => {
    return () => cancelTransitionRef.current();
  }, []);

  return {
    displayedValue,
    opacity,
    targetWidth,
  };
}
